import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Contract {
  id: string
  contract_number: string
  title: string
  party_name: string
  party_type: string
  contract_type: string
  status: string
  total_value: number
  start_date: string
  end_date: string
  auto_renew: boolean
}

interface Template {
  id: string
  name: string
  contract_type: string
  version: string
  is_active: boolean
}

export default function ContractsPage() {
  const [contracts, setContracts] = useState<Contract[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [contractsRes, templatesRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/contracts/'),
      fetch(API_BASE + '/contracts/templates'),
      fetch(API_BASE + '/contracts/statistics')
    ])
    if (contractsRes.ok) setContracts((await contractsRes.json()).contracts || [])
    if (templatesRes.ok) setTemplates((await templatesRes.json()).templates || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'pending_signature': return 'bg-yellow-100 text-yellow-800'
      case 'draft': return 'bg-gray-100 text-gray-600'
      case 'expired': return 'bg-red-100 text-red-800'
      case 'terminated': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate)
    const now = new Date()
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return diff
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Contract Management</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Contract</button>
            <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Templates</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Contracts</div>
              <div className="text-2xl font-bold">{statistics.total_contracts}</div>
              <div className="text-xs text-green-600">{statistics.active_contracts} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Value</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_contract_value)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Expiring Soon</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.expiring_soon}</div>
              <div className="text-xs text-gray-500">Within 30 days</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Signature</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.pending_signature}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Contracts</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Party</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {contracts.slice(0, 10).map(contract => {
                  const daysRemaining = getDaysRemaining(contract.end_date)
                  return (
                    <tr key={contract.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium">{contract.title}</div>
                        <div className="text-xs text-gray-500">{contract.contract_number}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{contract.party_name}</div>
                        <div className="text-xs text-gray-500 capitalize">{contract.party_type}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-green-600">{formatCurrency(contract.total_value)}</div>
                        <div className="text-xs text-gray-500">
                          {daysRemaining > 0 ? `${daysRemaining}d remaining` : 'Expired'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(contract.status)}`}>
                          {contract.status.replace(/_/g, ' ')}
                        </span>
                        {contract.auto_renew && <div className="text-xs text-blue-600 mt-1">Auto-renew</div>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Templates</h3>
            <div className="space-y-3">
              {templates.map(template => (
                <div key={template.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{template.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{template.contract_type}</div>
                  <div className="text-xs text-blue-600">v{template.version}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
